"use client";

import React, { useState, useRef, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "react-toastify";
import { useMiniCart } from "@/app/context/MiniCartContext";
import { addToCart } from "@/app/actions/cart";
 
interface VariantAttribute {
  price: number | string | null;
  compareAtPrice: number | string | null;
}

interface Variant {
  attributes: VariantAttribute;
}

interface ImageAttribute {
  src: string;
  alt?: string;
}

interface ProductImage {
  id: number;
  attributes: ImageAttribute;
}

interface ProductAttributes {
  Title: string;
  slug: string;
  description?: string;
  wb_product_variants?: {
    data: Variant[];
  };
  wb_product_images?: {
    data: ProductImage[];
  };
}

interface Product {
  id: number;
  attributes: ProductAttributes;
}

interface CollectionAttributes {
  title: string;
  bodyHtml?: string;
  wb_products?: {
    data: Product[];
  };
}

interface CollectionData {
  id: number;
  attributes: CollectionAttributes;
}
/* --- AutoScrollImage Optimized --- */
const AutoScrollImage = React.memo(
  ({ images, alt }: { images: ProductImage[]; alt: string }) => {
    const [idx, setIdx] = useState(0);

    React.useEffect(() => {
      if (!images || images.length <= 1) return;
      const interval = setInterval(() => {
        setIdx((prev) => (prev + 1) % images.length);
      }, 3000);
      return () => clearInterval(interval);
    }, [images?.length]);

    if (!images?.length) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-neutral-100">
          <span className="text-[10px] font-black uppercase tracking-widest text-gray-700">
            No Image
          </span>
        </div>
      );
    }

    const activeImage = images[idx];

    return (
      <Image
        key={activeImage.id}
        src={activeImage.attributes.src}
        alt={activeImage.attributes.alt || alt}
        fill
        quality={80}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
        className="object-cover transition-transform duration-700 ease-in-out group-hover/card:scale-110"
      />
    );
  }
);
AutoScrollImage.displayName = "AutoScrollImage";

/* --- Main Component --- */
const ProductCrousel_Layout1 = ({
  dataOfCollection,
  title,
  description,
}: {
  dataOfCollection: CollectionData;
  title: string;
  description: string;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [addingIds, setAddingIds] = useState<Set<number>>(new Set());
  const { open: openCart } = useMiniCart();

  const products = useMemo(() => {
    return dataOfCollection?.attributes?.wb_products?.data || [];
  }, [dataOfCollection]);

  const titleParts =
    title?.split(" ") || dataOfCollection?.attributes?.title?.split(" ") || [];
  const firstPart = titleParts.slice(0, -1).join(" ") || "";
  const lastPart = titleParts.slice(-1) || "";

  const formatPrice = (price: number | string | null | undefined) => {
    if (!price) return null;
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numPrice);
  };

  const getDiscount = (mrp: any, price: any) => {
    const numMrp = Number(mrp);
    const numPrice = Number(price);
    if (!numMrp || !numPrice || numMrp <= numPrice) return 0;
    return Math.round(((numMrp - numPrice) / numMrp) * 100);
  };

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -400 : 400,
      behavior: "smooth",
    });
  };

  const handleAddToCart = async (e: React.MouseEvent, product: Product) => {
    e.preventDefault();
    e.stopPropagation();
    const pid = product.id;
    if (addingIds.has(pid)) return;

    setAddingIds((prev) => new Set(prev).add(pid));
    try {
      await addToCart({ productId: pid, quantity: 1, variantId: null });
      toast.success("Added to cart!");
      openCart();
    } catch {
      toast.error("Failed to add to cart");
    } finally {
      setAddingIds((prev) => {
        const next = new Set(prev);
        next.delete(pid);
        return next;
      });
    }
  };

  if (!products.length) return null;

  return (
    <section className="w-full py-24 bg-white px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-black text-neutral-900 uppercase italic tracking-tighter text-center leading-[0.9]">
            {firstPart}{" "}
            <span style={{ color: "var(--secondary)" }}>{lastPart}</span>
          </h2>

          <div className="flex justify-center items-center">
            <span className="h-[2px] w-10 mx-3 bg-[var(--secondary)]"></span>
            <p className="text-[11px] font-bold text-gray-700 uppercase tracking-widest text-center">
              {description || "Expressive designs for small trendsetters"}
            </p>
            <span className="h-[2px] w-10 mx-3 bg-[var(--secondary)]"></span>
          </div>
        </div>

        <div className="group/carousel relative">
          {/* Navigation */}
          <button
            onClick={() => scroll("left")}
            className="absolute -left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white border border-neutral-200 shadow-xl opacity-0 group-hover/carousel:opacity-100 transition-all hidden md:block hover:bg-[var(--secondary)] hover:text-white"
            aria-label="Scroll Left"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            onClick={() => scroll("right")}
            className="absolute -right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white border border-neutral-200 shadow-xl opacity-0 group-hover/carousel:opacity-100 transition-all hidden md:block hover:bg-[var(--secondary)] hover:text-white"
            aria-label="Scroll Right"
          >
            <ChevronRight size={20} />
          </button>

          {/* Scroll Container */}
          <div
            ref={scrollRef}
            role="region"
            aria-label="Product Carousel"
            className="flex gap-6 overflow-x-auto scroll-smooth py-4 no-scrollbar"
          >
            {products.map((product) => {
              const attr = product.attributes;
              const images = attr.wb_product_images?.data || [];
              const variant = attr.wb_product_variants?.data?.[0]?.attributes;
              const price = variant?.price || 0;
              const mrp = variant?.compareAtPrice || 0;
              const discount = getDiscount(mrp, price);
              const isAdding = addingIds.has(product.id);

              return (
                <div
                  key={product.id}
                  className="flex-shrink-0 w-[280px] flex flex-col group/card"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-neutral-100 mb-6">
                    <Link
                      href={`/products/${attr.slug}`}
                      className="block h-full w-full"
                    >
                      <AutoScrollImage images={images} alt={attr.Title} />
                    </Link>

                    {discount > 0 && (
                      <div className="absolute top-0 left-0 text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest italic z-10 bg-[var(--secondary)]">
                        {discount}% OFF
                      </div>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover/card:translate-y-0 transition-transform duration-300 z-20">
                      <button
                        onClick={(e) => handleAddToCart(e, product)}
                        disabled={isAdding}
                        className="w-full bg-neutral-900 text-white py-4 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-all active:scale-95 hover:bg-[var(--secondary)]"
                      >
                        {isAdding ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Plus size={14} /> Quick Add
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col items-center space-y-1">
                    <Link
                      href={`/products/${attr.slug}`}
                      className="text-[12px] font-black text-neutral-900 uppercase tracking-tight line-clamp-1 text-center hover:text-[var(--secondary)] transition-colors"
                    >
                      {attr.Title}
                    </Link>

                    <div className="flex items-center gap-2">
                      <span
                        className="font-black text-lg italic tracking-tighter"
                        style={{ color: "var(--secondary)" }}
                      >
                        {formatPrice(price)}
                      </span>
                      {Number(mrp) > Number(price) && (
                        <span className="text-gray-700 line-through text-xs font-bold uppercase tracking-tighter">
                          {formatPrice(mrp)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductCrousel_Layout1;
